/**
 * Read a .pptx file and turn each slide into canvas elements (1280×720):
 * pictures, solid shapes, and text with the original color, size, weight, and alignment.
 * Older binary .ppt files are not OOXML and cannot be unpacked this way.
 */

export const PPTX_STAGE_WIDTH = 1280;
export const PPTX_STAGE_HEIGHT = 720;

export class PptxImportError extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
    this.name = "PptxImportError";
  }
}

export function isPowerPointFile(file) {
  const name = String(file?.name || "").toLowerCase();
  const type = String(file?.type || "").toLowerCase();
  return (
    name.endsWith(".pptx") ||
    name.endsWith(".ppt") ||
    type.includes("presentationml") ||
    type === "application/vnd.ms-powerpoint"
  );
}

export async function parsePptxFile(file, options = {}) {
  const buffer = await file.arrayBuffer();
  return parsePptxArrayBuffer(buffer, options);
}

export async function parsePptxArrayBuffer(buffer, options = {}) {
  const bytes = new Uint8Array(buffer);
  if (bytes.length >= 4 && bytes[0] === 0xd0 && bytes[1] === 0xcf && bytes[2] === 0x11 && bytes[3] === 0xe0) {
    throw new PptxImportError(
      "legacy-ppt",
      "This is an older PowerPoint .ppt file. Open it and save as .pptx to import the pictures and layout."
    );
  }
  if (!(bytes[0] === 0x50 && bytes[1] === 0x4b)) {
    throw new PptxImportError("invalid", "This file is not a PowerPoint presentation.");
  }

  const files = await unzip(bytes);
  const presentationPath = Object.keys(files).find((name) => /ppt\/presentation\.xml$/i.test(name));
  if (!presentationPath) {
    throw new PptxImportError("invalid", "This PowerPoint file has no slides.");
  }

  const presentation = parseXml(decodeXml(files[presentationPath]));
  const presRels = loadRels(files, presentationPath);
  const themePath = relTarget(presentationPath, presRels, "/theme");
  const theme = themePath && files[themePath] ? parseTheme(parseXml(decodeXml(files[themePath]))) : emptyTheme();
  const sizeNode = findFirst(presentation, "sldSz");
  const slideCx = numberAttr(sizeNode, "cx") || 12192000;
  const slideCy = numberAttr(sizeNode, "cy") || 6858000;

  const sldIdLst = findFirst(presentation, "sldIdLst");
  const slideRids = (sldIdLst?.children || [])
    .filter((node) => node.local === "sldId")
    .map((node) => attrNs(node, "id"))
    .filter(Boolean);

  if (!slideRids.length) {
    throw new PptxImportError("empty", "This PowerPoint file has no slides.");
  }

  options.onProgress?.({ phase: "read", done: 0, total: slideRids.length });
  const imageCache = new Map();
  const slides = [];
  let skippedMedia = 0;

  for (let index = 0; index < slideRids.length; index += 1) {
    options.onProgress?.({ phase: "slide", done: index, total: slideRids.length });
    const slidePath = relTargetById(presentationPath, presRels, slideRids[index]);
    if (!slidePath || !files[slidePath]) continue;
    const slideXml = parseXml(decodeXml(files[slidePath]));
    const slideRels = loadRels(files, slidePath);
    const layoutPath = relTarget(slidePath, slideRels, "/slideLayout");
    const layoutXml = layoutPath && files[layoutPath] ? parseXml(decodeXml(files[layoutPath])) : null;
    const layoutRels = layoutPath ? loadRels(files, layoutPath) : {};
    const masterPath = relTarget(layoutPath, layoutRels, "/slideMaster");
    const masterXml = masterPath && files[masterPath] ? parseXml(decodeXml(files[masterPath])) : null;
    const masterRels = masterPath ? loadRels(files, masterPath) : {};

    const elements = [];
    const pushImage = async (source, rels, box, extra) => {
      const image = await resolveImage(files, source, rels, imageCache, options);
      if (image?.skipped) {
        skippedMedia += 1;
        return;
      }
      if (!image?.url) return;
      elements.push(imageElement(elements.length, index, box, image.url, extra));
    };

    const slideBg = readBackground(slideXml, theme);
    const layoutBg = readBackground(layoutXml, theme);
    const masterBg = readBackground(masterXml, theme);
    const bg = slideBg || layoutBg || masterBg;
    const bgRels = slideBg ? slideRels : layoutBg ? layoutRels : masterRels;
    if (bg?.color && bg.color !== "#ffffff") {
      elements.push(
        imageElement(
          elements.length,
          index,
          { left: 0, top: 0, width: PPTX_STAGE_WIDTH, height: PPTX_STAGE_HEIGHT },
          svgRect(PPTX_STAGE_WIDTH, PPTX_STAGE_HEIGHT, bg.color),
          { isBackground: true }
        )
      );
    } else if (bg?.embed) {
      await pushImage(
        bg,
        bgRels,
        { left: 0, top: 0, width: PPTX_STAGE_WIDTH, height: PPTX_STAGE_HEIGHT },
        { isBackground: true }
      );
    }

    const layers = [
      { xml: masterXml, rels: masterRels, inherited: true },
      { xml: layoutXml, rels: layoutRels, inherited: true },
      { xml: slideXml, rels: slideRels, inherited: false },
    ];
    for (const layer of layers) {
      if (!layer.xml) continue;
      const tree = findFirst(layer.xml, "spTree");
      const nodes = visualNodes(tree);
      for (const node of nodes) {
        await walkShape(node, [], {
          files,
          rels: layer.rels,
          theme,
          slideCx,
          slideCy,
          slideIndex: index,
          inherited: layer.inherited,
          layoutXml,
          masterXml,
          elements,
          pushImage,
          onFont: options.onFont,
        });
      }
    }

    const title = slideTitle(elements, index);
    slides.push({ title, elements });
  }

  if (!slides.length) {
    throw new PptxImportError("empty", "This PowerPoint file has no slides.");
  }
  return { slides, skippedMedia };
}

function slideTitle(elements, index) {
  for (const element of elements) {
    if (element.type !== "TEXT") continue;
    const text = String(element.content || "")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (text) return text.slice(0, 180);
  }
  return `Slide ${index + 1}`;
}

async function walkShape(node, groups, ctx) {
  if (!node) return;
  if (node.local === "grpSp") {
    const xfrm = readXfrm(node);
    const next = xfrm ? groups.concat([groupFrame(xfrm)]) : groups;
    for (const childNode of visualNodes(node)) {
      await walkShape(childNode, next, ctx);
    }
    return;
  }
  if (attr(findFirst(node, "cNvPr"), "hidden") === "1" || attr(findFirst(node, "cNvPr"), "hidden") === "true") {
    return;
  }
  const ph = placeholderType(node);
  if (ctx.inherited && ph) return;

  const xfrm = readXfrm(node);
  if (!xfrm) return;
  const raw = readBox(xfrm);
  if (raw.cx < 1 || raw.cy < 1) return;
  const slideBox = toSlide(raw, groups);
  const box = toCanvas(slideBox, ctx.slideCx, ctx.slideCy);
  if (box.width < 1 || box.height < 1) return;
  const rotation = Math.abs(raw.rot) > 6000 ? raw.rot / 60000 : 0;

  const spPr = child(node, "spPr");
  const blipFill = child(node, "blipFill") || child(spPr, "blipFill");
  if (node.local === "pic" || blipFill) {
    const fill = blipFill || child(node, "blipFill");
    await ctx.pushImage(
      {
        embed: attrNs(child(fill, "blip"), "embed"),
        link: attrNs(child(fill, "blip"), "link"),
        srcRect: readSrcRect(child(fill, "srcRect")),
      },
      ctx.rels,
      box,
      { rotation }
    );
  } else if (node.local === "sp" || node.local === "cxnSp") {
    const fill = readPaint(spPr, ctx.theme);
    const stroke = readStroke(spPr, ctx.theme, ctx.slideCx);
    if (fill || stroke) {
      const geom = attr(child(spPr, "prstGeom"), "prst") || (node.local === "cxnSp" ? "line" : "rect");
      ctx.elements.push(
        imageElement(
          ctx.elements.length,
          ctx.slideIndex,
          box,
          shapeSvg(box.width, box.height, geom, fill, stroke),
          { rotation, imageFit: "fill" }
        )
      );
    }
  }

  const txBody = child(node, "txBody");
  if (!txBody && node.local === "graphicFrame") {
    const text = collectPlainText(node);
    if (text) {
      ctx.elements.push(textElement(ctx.elements.length, ctx.slideIndex, box, `<p>${escapeHtml(text)}</p>`, rotation));
    }
    return;
  }
  if (!txBody) return;
  const html = textHtml(txBody, {
    theme: ctx.theme,
    slideCx: ctx.slideCx,
    ph,
    shape: node,
    layoutXml: ctx.layoutXml,
    masterXml: ctx.masterXml,
    onFont: ctx.onFont,
  });
  if (!html) return;
  const textBox = insetBox(slideBox, txBody, ctx.slideCx, ctx.slideCy);
  ctx.elements.push(textElement(ctx.elements.length, ctx.slideIndex, textBox, html, rotation));
}

function textHtml(txBody, ctx) {
  const inherited = inheritedParagraphs(ctx);
  const ownList = child(txBody, "lstStyle");
  let auto = 1;
  const paragraphs = [];
  for (const para of txBody.children || []) {
    if (para.local !== "p") continue;
    const pPr = child(para, "pPr");
    const lvl = Number(attr(pPr, "lvl") || 0) || 0;
    const base = mergeStyle(
      mergeStyle(
        inherited[lvl] || inherited[0],
        styleFromPPr(child(ownList, `lvl${lvl + 1}pPr`) || child(ownList, "defPPr"), ctx.theme)
      ),
      styleFromPPr(pPr, ctx.theme)
    );
    const pieces = [];
    let anyText = false;
    for (const node of para.children || []) {
      if (node.local === "br") {
        pieces.push("<br>");
        continue;
      }
      if (node.local !== "r" && node.local !== "fld") continue;
      const text = nodeText(child(node, "t"));
      if (!text) continue;
      anyText = true;
      const run = {
        ...base.run,
        ...readRPr(child(node, "rPr"), ctx.theme),
      };
      pieces.push(styledSpan(text, run, ctx));
    }
    if (!anyText) continue;
    let prefix = "";
    if (base.bullet) {
      prefix = base.auto ? `${auto}. ` : `${base.char || "•"} `;
      if (base.auto) auto += 1;
    } else {
      auto = 1;
    }
    if (prefix) {
      pieces.unshift(styledSpan(prefix, base.run || {}, ctx));
    }
    const align = base.align || "left";
    const alignClass =
      align === "center" ? "ql-align-center" : align === "right" ? "ql-align-right" : align === "justify" ? "ql-align-justify" : "";
    paragraphs.push(
      `<p${alignClass ? ` class="${alignClass}"` : ""} style="text-align: ${align}; line-height: 1.05;">${pieces.join("")}</p>`
    );
  }
  return paragraphs.join("");
}

function styledSpan(text, run, ctx) {
  const font = ctx.onFont ? ctx.onFont(run.font || ctx.theme.minor || "Arial") : run.font || ctx.theme.minor || "Arial";
  const size = fontPx(run.size || 1800, ctx.slideCx);
  const color = toRgb(run.color || "#111111");
  let inner = escapeHtml(text).replace(/\n/g, "<br>");
  if (run.bold) inner = `<strong>${inner}</strong>`;
  if (run.italic) inner = `<em>${inner}</em>`;
  if (run.underline) inner = `<u>${inner}</u>`;
  return `<span style="color: ${color}; font-size: ${size}px; font-family: ${font};">${inner}</span>`;
}

function inheritedParagraphs(ctx) {
  const levels = [];
  const sources = [ctx.masterXml, ctx.layoutXml];
  for (const xml of sources) {
    const match = findPlaceholder(xml, ctx.ph);
    const list = child(child(match, "txBody"), "lstStyle");
    if (!list) continue;
    for (let lvl = 0; lvl < 8; lvl += 1) {
      const pPr = child(list, `lvl${lvl + 1}pPr`) || (lvl === 0 ? child(list, "defPPr") : null);
      const style = styleFromPPr(pPr, ctx.theme);
      if (style) levels[lvl] = mergeStyle(levels[lvl], style);
    }
  }
  return levels;
}

function findPlaceholder(xml, ph) {
  if (!xml || !ph) return null;
  const wanted =
    ph === "title" || ph === "ctrTitle"
      ? ["title", "ctrTitle"]
      : ph === "body" || ph === "subTitle" || ph === "obj"
        ? ["body", "subTitle", "obj"]
        : [ph];
  return descendants(xml, "sp").find((shape) => wanted.includes(placeholderType(shape) || "")) || null;
}

function mergeStyle(base, extra) {
  if (!extra) return base || {};
  if (!base) return extra;
  return {
    ...base,
    ...extra,
    run: { ...(base.run || {}), ...(extra.run || {}) },
    bullet: extra.bullet != null ? extra.bullet : base.bullet,
    auto: extra.auto != null ? extra.auto : base.auto,
    char: extra.char || base.char,
  };
}

function styleFromPPr(pPr, theme) {
  if (!pPr) return null;
  const style = {};
  const align = mapAlign(attr(pPr, "algn"));
  if (align) style.align = align;
  if (child(pPr, "buNone")) style.bullet = false;
  else if (child(pPr, "buAutoNum")) {
    style.bullet = true;
    style.auto = true;
  } else if (child(pPr, "buChar")) {
    style.bullet = true;
    style.auto = false;
    style.char = attr(child(pPr, "buChar"), "char") || "•";
  } else if (child(pPr, "buFont")) {
    style.bullet = true;
    style.char = "•";
  }
  const run = readRPr(child(pPr, "defRPr"), theme);
  if (Object.keys(run).length) style.run = run;
  return style;
}

function readRPr(rPr, theme) {
  if (!rPr) return {};
  const style = {};
  const size = numberAttr(rPr, "sz");
  if (size) style.size = size;
  const bold = flagAttr(rPr, "b");
  if (bold != null) style.bold = bold;
  const italic = flagAttr(rPr, "i");
  if (italic != null) style.italic = italic;
  if (attr(rPr, "u") != null) style.underline = attr(rPr, "u") !== "none" && attr(rPr, "u") !== "0";
  const color = colorOfFill(child(rPr, "solidFill"), theme);
  if (color) style.color = color;
  const face = attr(child(rPr, "latin"), "typeface") || attr(child(rPr, "ea"), "typeface") || attr(child(rPr, "cs"), "typeface");
  if (face) style.font = resolveFace(face, theme);
  return style;
}

function resolveFace(face, theme) {
  if (face === "+mj-lt" || face === "+mj-ea" || face === "+mj-cs") return theme.major || "Calibri Light";
  if (face === "+mn-lt" || face === "+mn-ea" || face === "+mn-cs") return theme.minor || "Calibri";
  return face;
}

function mapAlign(value) {
  if (value === "ctr") return "center";
  if (value === "r") return "right";
  if (value === "just") return "justify";
  if (value === "l") return "left";
  return null;
}

function fontPx(szHundredths, slideCx) {
  const points = (szHundredths || 1800) / 100;
  const pxPerInch = PPTX_STAGE_WIDTH / (slideCx / 914400);
  return Math.max(8, Math.min(200, Math.round((points * pxPerInch) / 72)));
}

function insetBox(slideBox, txBody, slideCx, slideCy) {
  const bodyPr = child(txBody, "bodyPr");
  const l = attr(bodyPr, "lIns") != null ? numberAttr(bodyPr, "lIns") : 91440;
  const t = attr(bodyPr, "tIns") != null ? numberAttr(bodyPr, "tIns") : 45720;
  const r = attr(bodyPr, "rIns") != null ? numberAttr(bodyPr, "rIns") : 91440;
  const b = attr(bodyPr, "bIns") != null ? numberAttr(bodyPr, "bIns") : 45720;
  const next = {
    x: slideBox.x + l,
    y: slideBox.y + t,
    cx: slideBox.cx - l - r,
    cy: slideBox.cy - t - b,
  };
  if (next.cx < slideBox.cx * 0.4 || next.cy < slideBox.cy * 0.4) {
    return toCanvas(slideBox, slideCx, slideCy);
  }
  return toCanvas(next, slideCx, slideCy);
}

function placeholderType(node) {
  const nv = child(node, "nvSpPr") || child(node, "nvPicPr") || child(node, "nvCxnSpPr") || child(node, "nvGraphicFramePr");
  const ph = child(child(nv, "nvPr"), "ph");
  if (!ph) return null;
  return attr(ph, "type") || "body";
}

function readBackground(root, theme) {
  if (!root) return null;
  const cSld = child(root, "cSld") || root;
  const bgPr = child(child(cSld, "bg"), "bgPr");
  if (!bgPr) return null;
  const solid = child(bgPr, "solidFill");
  if (solid) {
    const color = colorOfFill(solid, theme);
    return color ? { color } : null;
  }
  const blipFill = child(bgPr, "blipFill");
  if (blipFill) {
    return {
      embed: attrNs(child(blipFill, "blip"), "embed"),
      srcRect: readSrcRect(child(blipFill, "srcRect")),
    };
  }
  return null;
}

function readPaint(spPr, theme) {
  if (!spPr || child(spPr, "noFill")) return null;
  const solid = child(spPr, "solidFill");
  if (solid) return colorOfFill(solid, theme);
  const grad = child(spPr, "gradFill");
  if (grad) {
    const gs = findFirst(grad, "gs");
    return colorOfFill(gs, theme);
  }
  return null;
}

function readStroke(spPr, theme, slideCx) {
  const ln = child(spPr, "ln");
  if (!ln || child(ln, "noFill")) return null;
  const color = colorOfFill(child(ln, "solidFill"), theme);
  if (!color) return null;
  const emu = numberAttr(ln, "w") || 12700;
  const px = Math.max(1, Math.round((emu / slideCx) * PPTX_STAGE_WIDTH));
  return { color, px };
}

function colorOfFill(fillNode, theme) {
  if (!fillNode) return null;
  const srgb = child(fillNode, "srgbClr");
  if (srgb) return applyColorMods(normalizeHex(attr(srgb, "val")), srgb);
  const scheme = child(fillNode, "schemeClr");
  if (scheme) {
    const key = attr(scheme, "val");
    const base = theme.colors[key];
    if (!base) return null;
    return applyColorMods(base, scheme);
  }
  const sys = child(fillNode, "sysClr");
  if (sys) return normalizeHex(attr(sys, "lastClr"));
  return null;
}

function applyColorMods(hex, node) {
  if (!hex || !node) return hex;
  const lumMod = numberAttr(child(node, "lumMod"), "val");
  const lumOff = numberAttr(child(node, "lumOff"), "val");
  const shade = numberAttr(child(node, "shade"), "val");
  const tint = numberAttr(child(node, "tint"), "val");
  if (!lumMod && !lumOff && !shade && !tint) return hex;
  let { h, s, l } = hexToHsl(hex);
  if (lumMod) l *= lumMod / 100000;
  if (lumOff) l += lumOff / 100000;
  if (shade) l *= shade / 100000;
  if (tint) l = l * (tint / 100000) + (1 - tint / 100000);
  l = Math.max(0, Math.min(1, l));
  return hslToHex(h, s, l);
}

function readSrcRect(node) {
  if (!node) return null;
  return {
    l: numberAttr(node, "l"),
    t: numberAttr(node, "t"),
    r: numberAttr(node, "r"),
    b: numberAttr(node, "b"),
  };
}

function readXfrm(node) {
  const spPr = child(node, "spPr") || child(node, "grpSpPr");
  return child(spPr, "xfrm") || child(node, "xfrm");
}

function readBox(xfrm) {
  const off = child(xfrm, "off");
  const ext = child(xfrm, "ext");
  return {
    x: numberAttr(off, "x"),
    y: numberAttr(off, "y"),
    cx: numberAttr(ext, "cx"),
    cy: numberAttr(ext, "cy"),
    rot: numberAttr(xfrm, "rot"),
  };
}

function groupFrame(xfrm) {
  const off = child(xfrm, "off");
  const ext = child(xfrm, "ext");
  const chOff = child(xfrm, "chOff");
  const chExt = child(xfrm, "chExt");
  return {
    x: numberAttr(off, "x"),
    y: numberAttr(off, "y"),
    cx: numberAttr(ext, "cx") || 1,
    cy: numberAttr(ext, "cy") || 1,
    chX: numberAttr(chOff, "x"),
    chY: numberAttr(chOff, "y"),
    chCx: numberAttr(chExt, "cx") || numberAttr(ext, "cx") || 1,
    chCy: numberAttr(chExt, "cy") || numberAttr(ext, "cy") || 1,
  };
}

function toSlide(box, groups) {
  let x = box.x;
  let y = box.y;
  let cx = box.cx;
  let cy = box.cy;
  for (let i = groups.length - 1; i >= 0; i -= 1) {
    const group = groups[i];
    x = group.x + ((x - group.chX) * group.cx) / group.chCx;
    y = group.y + ((y - group.chY) * group.cy) / group.chCy;
    cx = (cx * group.cx) / group.chCx;
    cy = (cy * group.cy) / group.chCy;
  }
  return { x, y, cx, cy };
}

function toCanvas(box, slideCx, slideCy) {
  return {
    left: Math.round((box.x / slideCx) * PPTX_STAGE_WIDTH * 10) / 10,
    top: Math.round((box.y / slideCy) * PPTX_STAGE_HEIGHT * 10) / 10,
    width: Math.max(1, Math.round((box.cx / slideCx) * PPTX_STAGE_WIDTH * 10) / 10),
    height: Math.max(1, Math.round((box.cy / slideCy) * PPTX_STAGE_HEIGHT * 10) / 10),
  };
}

function imageElement(n, slideIndex, box, url, extra = {}) {
  return {
    type: "IMAGE",
    id: `PPTX_IMAGE__${slideIndex}__${n}`,
    position: { left: box.left, top: box.top },
    dimension: { width: `${box.width}px`, height: `${box.height}px` },
    content: url,
    imageFit: "fill",
    isBackground: Boolean(extra.isBackground),
    ...(extra.rotation ? { rotation: extra.rotation } : {}),
  };
}

function textElement(n, slideIndex, box, html, rotation) {
  return {
    type: "TEXT",
    id: `PPTX_TEXT__${slideIndex}__${n}`,
    position: { left: box.left, top: box.top },
    dimension: { width: `${box.width}px`, height: `${box.height}px` },
    content: html,
    ...(rotation ? { rotation } : {}),
  };
}

function shapeSvg(width, height, geom, fill, stroke) {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const fillAttr = fill || "none";
  const strokeAttr = stroke?.color || "none";
  const strokeWidth = stroke?.px || 0;
  let body;
  if (geom === "ellipse") {
    body = `<ellipse cx="${w / 2}" cy="${h / 2}" rx="${Math.max(0, w / 2 - strokeWidth)}" ry="${Math.max(0, h / 2 - strokeWidth)}" fill="${fillAttr}" stroke="${strokeAttr}" stroke-width="${strokeWidth}"/>`;
  } else if (geom === "roundRect") {
    const radius = Math.round(Math.min(w, h) * 0.16);
    body = `<rect x="${strokeWidth / 2}" y="${strokeWidth / 2}" width="${Math.max(0, w - strokeWidth)}" height="${Math.max(0, h - strokeWidth)}" rx="${radius}" fill="${fillAttr}" stroke="${strokeAttr}" stroke-width="${strokeWidth}"/>`;
  } else if (geom === "line" || geom === "straightConnector1") {
    body = `<line x1="0" y1="${h / 2}" x2="${w}" y2="${h / 2}" stroke="${strokeAttr === "none" ? fillAttr : strokeAttr}" stroke-width="${Math.max(strokeWidth, 1)}"/>`;
  } else {
    body = `<rect width="${w}" height="${h}" fill="${fillAttr}" stroke="${strokeAttr}" stroke-width="${strokeWidth}"/>`;
  }
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${body}</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function svgRect(width, height, color) {
  return shapeSvg(width, height, "rect", color, null);
}

async function resolveImage(files, source, rels, cache, options) {
  if (source?.link && /^https?:\/\//i.test(source.link)) {
    return { url: source.link };
  }
  const rel = source?.embed ? rels[source.embed] : null;
  if (!rel?.target) return null;
  const path = rel.target;
  const lower = path.toLowerCase();
  if (/\.(emf|wmf|wdp)$/i.test(lower)) return { skipped: true };
  const key = `${path}|${source.srcRect ? JSON.stringify(source.srcRect) : ""}`;
  if (cache.has(key)) return { url: cache.get(key) };
  const bytes = files[path];
  if (!bytes) return null;
  const type = mimeFor(lower);
  let blob = new Blob([bytes], { type });
  blob = await cropBlob(blob, source.srcRect);
  const name = path.split("/").pop() || "slide-image.png";
  const url = await options.uploadImage?.(blob, name);
  if (!url) return null;
  cache.set(key, url);
  return { url };
}

async function cropBlob(blob, srcRect) {
  if (!srcRect) return blob;
  const l = (srcRect.l || 0) / 100000;
  const t = (srcRect.t || 0) / 100000;
  const r = (srcRect.r || 0) / 100000;
  const b = (srcRect.b || 0) / 100000;
  if (l + t + r + b < 0.001) return blob;
  if (typeof document === "undefined" || typeof createImageBitmap !== "function") return blob;
  try {
    const bitmap = await createImageBitmap(blob);
    const sx = bitmap.width * l;
    const sy = bitmap.height * t;
    const sw = Math.max(1, bitmap.width * (1 - l - r));
    const sh = Math.max(1, bitmap.height * (1 - t - b));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(sw));
    canvas.height = Math.max(1, Math.round(sh));
    canvas.getContext("2d").drawImage(bitmap, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    const cropped = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    return cropped || blob;
  } catch (error) {
    return blob;
  }
}

function mimeFor(path) {
  if (path.endsWith(".png")) return "image/png";
  if (path.endsWith(".jpg") || path.endsWith(".jpeg")) return "image/jpeg";
  if (path.endsWith(".gif")) return "image/gif";
  if (path.endsWith(".webp")) return "image/webp";
  if (path.endsWith(".svg")) return "image/svg+xml";
  if (path.endsWith(".bmp")) return "image/bmp";
  return "application/octet-stream";
}

function parseTheme(root) {
  const theme = emptyTheme();
  const scheme = findFirst(root, "clrScheme");
  for (const entry of scheme?.children || []) {
    const hex = normalizeHex(attr(child(entry, "srgbClr"), "val") || attr(child(entry, "sysClr"), "lastClr"));
    if (hex) theme.colors[entry.local] = hex;
  }
  theme.colors.tx1 = theme.colors.tx1 || theme.colors.dk1;
  theme.colors.bg1 = theme.colors.bg1 || theme.colors.lt1;
  theme.colors.tx2 = theme.colors.tx2 || theme.colors.dk2;
  theme.colors.bg2 = theme.colors.bg2 || theme.colors.lt2;
  theme.major = attr(child(findFirst(root, "majorFont"), "latin"), "typeface") || theme.major;
  theme.minor = attr(child(findFirst(root, "minorFont"), "latin"), "typeface") || theme.minor;
  return theme;
}

function emptyTheme() {
  return {
    colors: {
      dk1: "#000000",
      lt1: "#ffffff",
      dk2: "#1f4e79",
      lt2: "#eeeeee",
      accent1: "#4472c4",
      accent2: "#ed7d31",
      accent3: "#a9d08e",
      accent4: "#ffc000",
      accent5: "#5b9bd5",
      accent6: "#70ad47",
      tx1: "#000000",
      bg1: "#ffffff",
      tx2: "#1f4e79",
      bg2: "#eeeeee",
    },
    major: "Calibri Light",
    minor: "Calibri",
  };
}

function loadRels(files, filePath) {
  if (!filePath) return {};
  const relXml = files[relsPath(filePath)];
  if (!relXml) return {};
  const root = parseXml(decodeXml(relXml));
  const rels = {};
  for (const rel of root?.children || []) {
    if (rel.local !== "Relationship") continue;
    const id = attr(rel, "Id") || attr(rel, "id");
    const target = attr(rel, "Target") || attr(rel, "target");
    const type = attr(rel, "Type") || attr(rel, "type") || "";
    if (!id || !target) continue;
    rels[id] = { type, target: resolveZipPath(filePath, target) };
  }
  return rels;
}

function relTarget(filePath, rels, typePart) {
  const match = Object.values(rels || {}).find((rel) => String(rel.type).includes(typePart));
  return match?.target || null;
}

function relTargetById(filePath, rels, id) {
  return rels?.[id]?.target || null;
}

function relsPath(filePath) {
  const parts = filePath.split("/");
  const name = parts.pop();
  return [...parts, "_rels", `${name}.rels`].join("/");
}

function resolveZipPath(fromFile, target) {
  if (!target) return "";
  const cleaned = target.split("\\").join("/");
  if (cleaned.startsWith("/")) return cleaned.replace(/^\/+/, "");
  const parts = fromFile.split("/").slice(0, -1);
  for (const part of cleaned.split("/")) {
    if (part === "..") parts.pop();
    else if (part && part !== ".") parts.push(part);
  }
  return parts.join("/");
}

function visualNodes(node) {
  const out = [];
  for (const childNode of node?.children || []) {
    if (childNode.local === "AlternateContent") {
      const choice = child(childNode, "Choice");
      const fallback = child(childNode, "Fallback");
      const chosen = visualNodes(choice);
      out.push(...(chosen.length ? chosen : visualNodes(fallback)));
      continue;
    }
    if (["sp", "pic", "grpSp", "cxnSp", "graphicFrame"].includes(childNode.local)) out.push(childNode);
  }
  return out;
}

function collectPlainText(node) {
  return descendants(node, "t")
    .map((item) => nodeText(item))
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

function nodeText(node) {
  if (!node) return "";
  return node.text || "";
}

function child(node, local) {
  if (!node?.children) return null;
  return node.children.find((entry) => entry.local === local) || null;
}

function findFirst(node, local) {
  if (!node) return null;
  if (node.local === local) return node;
  for (const entry of node.children || []) {
    const found = findFirst(entry, local);
    if (found) return found;
  }
  return null;
}

function descendants(node, local, acc = []) {
  for (const entry of node?.children || []) {
    if (entry.local === local) acc.push(entry);
    descendants(entry, local, acc);
  }
  return acc;
}

function attr(node, name) {
  if (!node?.attrs || node.attrs[name] == null) return null;
  return node.attrs[name];
}

function attrNs(node, local) {
  if (!node?.attrs) return null;
  const key = Object.keys(node.attrs).find((name) => name.endsWith(`:${local}`));
  if (key) return node.attrs[key];
  return attr(node, local);
}

function numberAttr(node, name) {
  const value = Number(attr(node, name));
  return Number.isFinite(value) ? value : 0;
}

function flagAttr(node, name) {
  const value = attr(node, name);
  if (value == null) return null;
  return value === "1" || value === "true";
}

function normalizeHex(value) {
  if (!value) return null;
  const hex = String(value).replace("#", "").trim();
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
  return `#${hex.toLowerCase()}`;
}

function toRgb(hex) {
  const normalized = normalizeHex(hex) || "#111111";
  const value = normalized.slice(1);
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgb(${r}, ${g}, ${b})`;
}

function hexToHsl(hex) {
  const normalized = normalizeHex(hex) || "#000000";
  let r = parseInt(normalized.slice(1, 3), 16) / 255;
  let g = parseInt(normalized.slice(3, 5), 16) / 255;
  let b = parseInt(normalized.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return { h, s, l };
}

function hslToHex(h, s, l) {
  let r;
  let g;
  let b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue = (p, q, t) => {
      let tt = t;
      if (tt < 0) tt += 1;
      if (tt > 1) tt -= 1;
      if (tt < 1 / 6) return p + (q - p) * 6 * tt;
      if (tt < 1 / 2) return q;
      if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue(p, q, h + 1 / 3);
    g = hue(p, q, h);
    b = hue(p, q, h - 1 / 3);
  }
  const channel = (n) => Math.max(0, Math.min(255, Math.round(n * 255))).toString(16).padStart(2, "0");
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function decodeXml(bytes) {
  return new TextDecoder("utf-8").decode(bytes).replace(/^\uFEFF/, "");
}

function parseXml(source) {
  const xml = String(source)
    .replace(/<\?xml[\s\S]*?\?>/g, "")
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<!DOCTYPE[\s\S]*?>/g, "");
  let index = 0;

  const skipSpace = () => {
    while (index < xml.length && /\s/.test(xml[index])) index += 1;
  };
  const readName = () => {
    const start = index;
    while (index < xml.length && /[^\/\s>=]/.test(xml[index])) index += 1;
    return xml.slice(start, index);
  };
  const readValue = () => {
    skipSpace();
    const quote = xml[index];
    if (quote !== '"' && quote !== "'") return "";
    index += 1;
    const start = index;
    while (index < xml.length && xml[index] !== quote) index += 1;
    const value = xml.slice(start, index);
    index += 1;
    return decodeEntities(value);
  };
  const parseNode = () => {
    skipSpace();
    if (xml[index] !== "<" || xml.startsWith("</", index)) return null;
    index += 1;
    const name = readName();
    const attrs = {};
    while (index < xml.length) {
      skipSpace();
      if (xml[index] === "/" && xml[index + 1] === ">") {
        index += 2;
        return makeNode(name, attrs, [], "");
      }
      if (xml[index] === ">") {
        index += 1;
        break;
      }
      const key = readName();
      skipSpace();
      if (xml[index] === "=") index += 1;
      attrs[key] = readValue();
    }
    const children = [];
    let text = "";
    while (index < xml.length) {
      if (xml.startsWith("</", index)) {
        index += 2;
        readName();
        skipSpace();
        if (xml[index] === ">") index += 1;
        break;
      }
      if (xml.startsWith("<![CDATA[", index)) {
        index += 9;
        const end = xml.indexOf("]]>", index);
        text += xml.slice(index, end === -1 ? xml.length : end);
        index = end === -1 ? xml.length : end + 3;
        continue;
      }
      if (xml[index] === "<") {
        const childNode = parseNode();
        if (childNode) children.push(childNode);
        continue;
      }
      const start = index;
      while (index < xml.length && xml[index] !== "<") index += 1;
      text += decodeEntities(xml.slice(start, index));
    }
    return makeNode(name, attrs, children, text);
  };

  skipSpace();
  return parseNode();
}

function makeNode(name, attrs, children, text) {
  const local = name.includes(":") ? name.split(":").pop() : name;
  return { name, local, attrs, children, text };
}

function decodeEntities(value) {
  return value
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, num) => String.fromCodePoint(parseInt(num, 10)))
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&");
}

async function unzip(bytes) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let eocd = -1;
  const min = Math.max(0, bytes.length - 22 - 65535);
  for (let i = bytes.length - 22; i >= min; i -= 1) {
    if (view.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new PptxImportError("invalid", "This PowerPoint file could not be opened.");
  const count = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);
  const files = {};
  for (let n = 0; n < count; n += 1) {
    if (view.getUint32(offset, true) !== 0x02014b50) break;
    const method = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const localOffset = view.getUint32(offset + 42, true);
    const name = new TextDecoder("utf-8")
      .decode(bytes.subarray(offset + 46, offset + 46 + nameLength))
      .split("\\")
      .join("/");
    offset += 46 + nameLength + extraLength + commentLength;
    if (!name || name.endsWith("/")) continue;
    if (compressedSize === 0xffffffff) {
      throw new PptxImportError("invalid", "This PowerPoint file is too large to import in the browser.");
    }
    const localNameLength = view.getUint16(localOffset + 26, true);
    const localExtraLength = view.getUint16(localOffset + 28, true);
    const dataStart = localOffset + 30 + localNameLength + localExtraLength;
    const compressed = bytes.subarray(dataStart, dataStart + compressedSize);
    if (method === 0) files[name] = compressed;
    else if (method === 8) files[name] = new Uint8Array(await inflateRaw(compressed));
    else throw new PptxImportError("invalid", "This PowerPoint file uses a compression method the importer cannot read.");
  }
  return files;
}

async function inflateRaw(bytes) {
  if (typeof DecompressionStream !== "function") {
    throw new PptxImportError("invalid", "This browser cannot unpack a PowerPoint file.");
  }
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Response(stream).arrayBuffer();
}
